import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, BookOpen, Archive, ArchiveRestore, Radio, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Dialog } from '../components/ui/Dialog';
import { Input, Textarea, Label } from '../components/ui/Input';
import { PageLoader } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { apiFetch, ApiError } from '../lib/apiFetch';
import { formatUzs } from '../lib/format';
import { useToast } from '../components/ui/Toast';
import { useT } from '../i18n';

interface Course {
  id: number;
  channel_id: number;
  title: string;
  description: string | null;
  subject: string | null;
  exam_target: string | null;
  format: string | null;
  rules_text: string | null;
  duration_days: number;
  price_uzs: string;
  access_method: string;
  grace_period_hours: number;
  reminder_days: number[];
  auto_kick_enabled: boolean;
  is_active: boolean;
}

interface Channel {
  id: number;
  chat_id: number;
  title: string;
}

const CourseSchema = z.object({
  channel_chat_id: z.coerce.number().int(),
  title: z.string().min(1),
  description: z.string().optional(),
  subject: z.string().optional(),
  exam_target: z.string().optional(),
  duration_days: z.coerce.number().int().positive(),
  price_uzs: z.coerce.number().nonnegative(),
  rules_text: z.string().optional(),
  access_method: z.enum(['invite_link', 'join_request']).default('join_request'),
  grace_period_hours: z.coerce.number().int().nonnegative().default(48),
  auto_kick_enabled: z.boolean().default(true),
});

type CourseFormValues = z.infer<typeof CourseSchema>;

export function Courses() {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Course | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();

  const courses = useQuery({
    queryKey: ['courses', 'all'],
    queryFn: () => apiFetch<{ courses: Course[] }>('/api/admin/courses'),
  });

  const channels = useQuery({
    queryKey: ['channels'],
    queryFn: () => apiFetch<{ channels: Channel[] }>('/api/admin/channels'),
  });

  const channelById = useMemo(() => {
    const m = new Map<number, Channel>();
    channels.data?.channels.forEach((c) => m.set(c.id, c));
    return m;
  }, [channels.data]);

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiFetch(`/api/admin/courses/${id}/toggle`, {
        method: 'POST',
        body: { is_active: isActive },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['courses'] }),
    onError: (err) => toast.error((err as ApiError).message ?? t('courses.toggleFailed')),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium text-slate-600">{t('courses.heading')}</h2>
        <Button onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          {t('courses.new')}
        </Button>
      </div>

      {courses.isLoading ? (
        <PageLoader />
      ) : !courses.data || courses.data.courses.length === 0 ? (
        <EmptyState
          icon={<BookOpen className="h-10 w-10" />}
          title={t('courses.emptyTitle')}
          description={t('courses.emptyDescription')}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {courses.data.courses.map((c) => (
            <Card key={c.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle>{c.title}</CardTitle>
                  <Badge variant={c.is_active ? 'success' : 'muted'}>
                    {c.is_active ? t('common.active') : t('common.archived')}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-slate-600">
                {channelById.get(c.channel_id) && (
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Radio className="h-3 w-3" />
                    <span>
                      {t('courses.channel')}: <b>{channelById.get(c.channel_id)!.title}</b>
                    </span>
                  </div>
                )}
                <div>
                  {formatUzs(c.price_uzs)} · {c.duration_days} {t('common.dayShort')}
                </div>
                <div className="text-xs">
                  {t('courses.method')}: <span className="font-mono">{c.access_method}</span>
                </div>
                <div className="text-xs">
                  {t('courses.grace')}: {c.grace_period_hours} {t('common.hourShort')}
                </div>
                <div className="text-xs">
                  {t('courses.reminders')}: {c.reminder_days.join(', ')} {t('common.dayShort')}
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(c)}>
                    <Pencil className="h-4 w-4" />
                    {t('common.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggle.mutate({ id: c.id, isActive: !c.is_active })}
                    loading={toggle.isPending && toggle.variables?.id === c.id}
                  >
                    {c.is_active ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
                    {c.is_active ? t('common.toArchive') : t('common.unarchive')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={creating} onClose={() => setCreating(false)} title={t('courses.new')} size="lg">
        <CourseForm
          channels={channels.data?.channels ?? []}
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['courses'] });
          }}
        />
      </Dialog>

      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title={t('courses.dialogEdit', { title: editing?.title ?? '' })}
        size="lg"
      >
        {editing && (
          <CourseForm
            channels={channels.data?.channels ?? []}
            existing={editing}
            onCancel={() => setEditing(null)}
            onCreated={() => {
              setEditing(null);
              qc.invalidateQueries({ queryKey: ['courses'] });
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function CourseForm({
  channels,
  existing,
  onCancel,
  onCreated,
}: {
  channels: Channel[];
  existing?: Course;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const isEdit = !!existing;
  const existingChannel = existing
    ? channels.find((ch) => ch.id === existing.channel_id)
    : null;

  const toast = useToast();
  const t = useT();

  const { register, handleSubmit, formState } = useForm<CourseFormValues>({
    resolver: zodResolver(CourseSchema),
    defaultValues: existing
      ? {
          channel_chat_id: existingChannel?.chat_id ?? 0,
          title: existing.title,
          description: existing.description ?? '',
          subject: existing.subject ?? '',
          exam_target: existing.exam_target ?? '',
          duration_days: existing.duration_days,
          price_uzs: Number(existing.price_uzs),
          rules_text: existing.rules_text ?? '',
          access_method: existing.access_method as 'invite_link' | 'join_request',
          grace_period_hours: existing.grace_period_hours,
          auto_kick_enabled: existing.auto_kick_enabled,
        }
      : { duration_days: 30, grace_period_hours: 48, access_method: 'join_request', auto_kick_enabled: true },
  });

  const submit = useMutation({
    mutationFn: (data: CourseFormValues) => {
      if (isEdit && existing) {
        // PATCH с DIFF — только реально изменённые поля. dirtyFields содержит ключи,
        // которые пользователь трогал (после первого изменения значения).
        const dirtyKeys = Object.keys(formState.dirtyFields) as (keyof CourseFormValues)[];
        const patch: Partial<CourseFormValues> = {};
        for (const k of dirtyKeys) {
          if (k === 'channel_chat_id') continue; // канал нельзя менять
          (patch as Record<string, unknown>)[k] = data[k];
        }
        if (Object.keys(patch).length === 0) {
          // Ничего не изменилось — просто закрываем
          return Promise.resolve({ ok: true } as const);
        }
        return apiFetch(`/api/admin/courses/${existing.id}`, {
          method: 'PATCH',
          body: patch,
        });
      }
      return apiFetch('/api/admin/courses', {
        method: 'POST',
        body: { ...data, reminder_days: [7] },
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? t('courses.updated') : t('courses.created'));
      onCreated();
    },
    onError: (err) => toast.error((err as ApiError).message ?? t('courses.saveFailed')),
  });

  return (
    <form onSubmit={handleSubmit((d) => submit.mutate(d))} className="space-y-3">
      <div>
        <Label>{t('courses.formChannel')}</Label>
        {isEdit ? (
          <div className="mt-1 flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600">
            <Radio className="mr-2 h-3 w-3" />
            {existingChannel ? `${existingChannel.title} (${existingChannel.chat_id})` : '—'}
            <span className="ml-2 text-xs text-slate-400">{t('courses.formChannelLocked')}</span>
          </div>
        ) : (
          <select
            {...register('channel_chat_id', { valueAsNumber: true })}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
          >
            <option value="">{t('courses.formChannelPick')}</option>
            {channels.map((c) => (
              <option key={c.id} value={c.chat_id}>
                {c.title} ({c.chat_id})
              </option>
            ))}
          </select>
        )}
      </div>
      <div>
        <Label>{t('courses.formTitle')}</Label>
        <Input {...register('title')} className="mt-1" />
      </div>
      <div>
        <Label>{t('courses.formDescription')}</Label>
        <Textarea {...register('description')} className="mt-1" rows={3} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>{t('courses.formPrice')}</Label>
          <Input type="number" {...register('price_uzs', { valueAsNumber: true })} className="mt-1" />
        </div>
        <div>
          <Label>{t('courses.formDuration')}</Label>
          <Input type="number" {...register('duration_days', { valueAsNumber: true })} className="mt-1" />
        </div>
        <div>
          <Label>{t('courses.formAccessMethod')}</Label>
          <select
            {...register('access_method')}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
          >
            <option value="join_request">join_request</option>
            <option value="invite_link">invite_link</option>
          </select>
        </div>
        <div>
          <Label>{t('courses.formGrace')}</Label>
          <Input type="number" {...register('grace_period_hours', { valueAsNumber: true })} className="mt-1" />
        </div>
      </div>
      <div>
        <Label>{t('courses.formRules')}</Label>
        <Textarea {...register('rules_text')} className="mt-1" rows={4} />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={submit.isPending}>
          {isEdit ? t('common.save') : t('common.create')}
        </Button>
      </div>
    </form>
  );
}
